/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Tooltip — NS/Tractie Tooltip component overrides.
//
// Family-anatomy: surface-feedback / popover-family. Tractie tooltip is een
// non-modal popover met `placement: 'top'` hardcoded en optionele arrow.
// We mappen MUI's defaults (`bottom` placement, `arrow=false`, system theme bg)
// naar de Tractie-spec.
//
// Tractie source-of-truth:
//   - node_modules/@tractie/components/src/custom-elements/tooltip/src/tooltip.css.js
//   - placement hardcoded 'top' in tooltip.js:96
//
// Tractie default appearance (inverted: donker bg + lichte tekst):
//   --tractie-tooltip-color:          var(--tractie-color-content-default-inverse)  (WIT op donker)
//   --tractie-tooltip-background-color: var(--tractie-color-background-default-inverse)  (DONKER)
//   --tractie-tooltip-padding-block:  var(--tractie-spacing-spacer-2)  (8px)
//   --tractie-tooltip-padding-inline: var(--tractie-spacing-spacer-4)  (16px)
//   --tractie-tooltip-font-size:      0.875rem  (14px)
//   border-radius:                    var(--tractie-border-radius-m)  (6px)
//   box-shadow:                       var(--tractie-web-shadow-elevated-2)
//   max-width:                        min(90%, 90dvw)
//   arrow-width:                      16px
//   arrow-height:                     8px
//
// Tractie subtle appearance:
//   --tractie-tooltip-background-color: var(--tractie-color-background-default)     (LICHT)
//   --_border-color:                    var(--tractie-color-border-subtle)
//   --tractie-tooltip-color:            var(--tractie-color-content-default)
//
// MUI v4 Tooltip class-keys:
//   popper | popperInteractive | popperArrow | tooltip | tooltipArrow | arrow |
//   touch | tooltipPlacementLeft | tooltipPlacementRight | tooltipPlacementTop |
//   tooltipPlacementBottom

// Tractie defaults — uitgedrukt via NS tokens of CSS custom-property fallback
// op de raw Tractie-tokens. NS tokens hebben prefs voor Tractie-tokens (NS
// theme bouwt voort op Tractie tokens.css).

export const MuiTooltipLightOverrides = {
  // ── Popper container — base attributes that affect both default & subtle ──
  popper: {
    // Allow override-scoping via data-ns-tooltip-appearance on PopperProps.
    pointerEvents: 'none',
  },

  // ── Tooltip body (default appearance: inverted) ──────────────────────────
  tooltip: {
    backgroundColor:
      'var(--tractie-color-background-default-inverse, var(--ns-color-bg-inverse, #252528))',
    color:
      'var(--tractie-color-content-default-inverse, var(--ns-color-fg-inverse, #ffffff))',
    // Tractie's `--tractie-tooltip-font-size: 0.875rem` token is set on :host
    // but never APPLIED to the rendered `.content` element (tooltip.css.js sets
    // `--_font-size` but no `font-size: var(--_font-size)` rule). Slot content
    // therefore inherits page-default 16px. We mirror Tractie LIVE rendering
    // (Bart-regel #10: Tractie-leading via observed behavior).
    fontSize: '1rem',
    fontFamily: 'system-ui, sans-serif',
    fontWeight: 400,
    lineHeight: 1.5,
    // Match Tractie text-rendering pipeline. Backstage's MUI baseline applies
    // `text-rendering: optimizeLegibility` (kerning on) and font-smoothing
    // `auto`, which compresses "Tooltip" ~7px narrower than Tractie. Tractie's
    // slot content inherits the page default `optimizeSpeed` + `antialiased`.
    textRendering: 'optimizeSpeed',
    WebkitFontSmoothing: 'antialiased',
    padding:
      'var(--tractie-spacing-spacer-2, 8px) var(--tractie-spacing-spacer-4, 16px)',
    borderRadius: 'var(--tractie-border-radius-m, 6px)',
    boxShadow:
      'var(--tractie-web-shadow-elevated-2, 0 4px 8px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08))',
    // Tractie spec is `min(90%, 90dvw)` but `90%` resolves against the
    // containing block. Tractie's `.content` is `position: absolute` so 90%
    // = viewport-relative. MUI's tooltip is static-positioned inside Popper,
    // so `90%` resolves against Popper width, which itself sizes to the
    // tooltip — circular shrink to 90% of natural width. Use viewport units
    // to evaluate against the viewport like Tractie does in practice.
    maxWidth: 'min(90vw, 90dvw)',
    // width: max-content lets the bbox expand to fit the text content up to
    // maxWidth. Without this, MUI v4 Popper computes a narrower width that
    // forces character-break on single-word tooltips like "Tooltip" → "Toolti / p".
    width: 'max-content',
    // Match Tractie: overflow at whitespace only, no character-break.
    overflowWrap: 'normal' as const,
    border: '1px solid transparent',

    // Subtle variant — light bg + border + dark text. Activated via
    // PopperProps={{ 'data-ns-tooltip-appearance': 'subtle' }} on the wrapping
    // popper element. Tooltip-element is descendant of popper, so this
    // selector targets the tooltip body when popper has the attribute.
    '[data-ns-tooltip-appearance="subtle"] &': {
      backgroundColor:
        'var(--tractie-color-background-default, var(--ns-color-bg-default, #ffffff))',
      color:
        'var(--tractie-color-content-default, var(--ns-color-fg-default, #252528))',
      borderColor:
        'var(--tractie-color-border-subtle, var(--ns-color-border-subtle, #e5e5e5))',
    },
  },

  // ── Arrow ────────────────────────────────────────────────────────────────
  // Tractie spec: `--tooltip-arrow-width: 16px`, `--tooltip-arrow-height: 8px`
  // (2:1 flat triangle). MUI v4 default: width 1em, height 0.71em (square
  // rotated 45° = sqrt(2)/2 ratio). Override to match Tractie's 2:1.
  arrow: {
    color:
      'var(--tractie-color-background-default-inverse, var(--ns-color-bg-inverse, #252528))',
    width: '16px',
    height: '8px',
    fontSize: '8px',

    '[data-ns-tooltip-appearance="subtle"] &': {
      color:
        'var(--tractie-color-background-default, var(--ns-color-bg-default, #ffffff))',
    },
  },

  // ── Placement offsets ────────────────────────────────────────────────────
  // Tractie applies 8px arrow-clearance via Floating UI middleware. MUI v4
  // defaults base `margin: 24px 0` (Tooltip.js:tooltipPlacementTop) and
  // overrides via `theme.breakpoints.up('sm')` to `margin: 14px 0` at viewport
  // ≥600px. Override BOTH base AND sm-breakpoint so 8px wins everywhere.
  // Tractie's Floating UI middleware places the BODY 11px above anchor,
  // regardless of arrow presence (8px design-spec clearance + ~3px from the
  // Floating UI offset rounding). Empirically verified C1 (arrow) and C2
  // (no-arrow): both put Tractie body at anchor.top - body.h - 11. Override
  // both base and sm-breakpoint (the latter is MUI v4's `theme.breakpoints.
  // up('sm')` fallback that defaults to `14px 0`).
  tooltipPlacementTop: {
    margin: '11px 0',
    '@media (min-width: 600px)': { margin: '11px 0' },
  },
  tooltipPlacementBottom: {
    margin: '11px 0',
    '@media (min-width: 600px)': { margin: '11px 0' },
  },
  tooltipPlacementLeft: {
    margin: '0 11px',
    '@media (min-width: 600px)': { margin: '0 11px' },
  },
  tooltipPlacementRight: {
    margin: '0 11px',
    '@media (min-width: 600px)': { margin: '0 11px' },
  },
} as const;

// Dark mode: NS tokens are mode-aware via tokens.css, so the same overrides
// apply. (Tractie inverse-tokens automatically flip under [data-theme="dark"].)
export const MuiTooltipDarkOverrides = MuiTooltipLightOverrides;
